import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '@/lib/http';
import {
  buscarAgoraRespostaSchema,
  consultaChaveRespostaSchema,
  estadoCapturaSchema,
  type ListarExecucoesCapturaQuery,
  paginaExecucoesCapturaSchema,
  painelCapturaSchema,
} from '@fiscoai/contracts';

import { intervaloAtualizacao, POLL_EXECUTANDO_MS } from './atualizacao';

/**
 * Captura na SEFAZ (spec 0006): painel, estado por empresa, histórico e as duas ações. Vive em
 * `lib/` porque o painel, a aba da empresa e a lista "Aguardando XML" usam as mesmas consultas.
 */

export function usePainelCaptura(empresaIds: readonly string[]) {
  return useQuery({
    queryKey: ['captura', 'painel', empresaIds],
    queryFn: ({ signal }) =>
      request('/captura', { schema: painelCapturaSchema, query: { empresaIds }, signal }),
    // Acorda logo depois do próximo marco (liberação, próxima busca), 3 s durante uma busca.
    refetchInterval: (q) => intervaloAtualizacao(q.state.data?.empresas ?? []),
  });
}

export function useEstadoCaptura(empresaId: string, habilitado = true) {
  return useQuery({
    queryKey: ['captura', 'estado', empresaId],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      request(`/empresas/${empresaId}/captura`, { schema: estadoCapturaSchema, signal }),
    refetchInterval: (q) => intervaloAtualizacao(q.state.data ? [q.state.data] : []),
  });
}

export type FiltrosExecucoes = Pick<
  ListarExecucoesCapturaQuery,
  'pagina' | 'porPagina' | 'ordem' | 'status'
>;

/** A primeira página do histórico: é ela que o "Buscar agora" acompanha. */
export const EXECUCOES_RECENTES: FiltrosExecucoes = { pagina: 1, porPagina: 10, ordem: 'desc' };

/**
 * Uma página do histórico. Enquanto alguma execução da página roda (ou `acompanhar` pede), se
 * atualiza sozinha; a mesma consulta serve à tabela e ao acompanhamento — sem poll duplicado.
 */
export function useExecucoesCaptura(
  empresaId: string,
  filtros: FiltrosExecucoes,
  opcoes: { habilitado?: boolean; acompanhar?: boolean } = {},
) {
  const { habilitado = true, acompanhar = false } = opcoes;
  return useQuery({
    queryKey: ['captura', 'execucoes', empresaId, filtros],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      request(`/empresas/${empresaId}/captura/execucoes`, {
        schema: paginaExecucoesCapturaSchema,
        query: { ...filtros },
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      acompanhar || q.state.data?.items.some((e) => e.status === 'EXECUTANDO')
        ? POLL_EXECUTANDO_MS
        : false,
  });
}

/** Dispara o "Buscar agora" (202 com o id da execução); o acompanhamento é do `useBuscarAgora`. */
export function useDispararBusca(empresaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request(`/empresas/${empresaId}/captura/buscar-agora`, {
        method: 'POST',
        schema: buscarAgoraRespostaSchema,
      }),
    onSettled: () => cliente.invalidateQueries({ queryKey: ['captura'] }),
  });
}

/** "Buscar nota pela chave" (consChNFe): a lista de chaves e a de documentos podem mudar. */
export function useConsultarChave(empresaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (chave: string) =>
      request(`/empresas/${empresaId}/captura/consultas-chave`, {
        method: 'POST',
        body: { chave },
        schema: consultaChaveRespostaSchema,
      }),
    onSettled: async () => {
      await Promise.all(
        ['captura', 'chaves-pendentes', 'documentos'].map((k) =>
          cliente.invalidateQueries({ queryKey: [k] }),
        ),
      );
    },
  });
}
